/**
 * Giving a role to somebody, and taking it away (WALLIYO-0131).
 *
 * These endpoints already existed; nothing on this screen could reach them, so
 * managing who holds a role meant going to Employees and finding the person.
 * That is the wrong way round: you decide about a role while looking at the
 * role.
 *
 * Both are the console's ordinary write: a reason, an Idempotency-Key per
 * click, and the server's refusal shown rather than guessed at — it checks
 * that the person granting may grant everything the role holds, which no form
 * here can know.
 */

import type { StaffMemberListItem, StaffRoleListItem } from "@wallyo/contracts/console";
import { useState } from "react";

import { assignStaffRole, removeStaffRole } from "../../api/console-api";
import { useApi } from "../../api/context";
import { t } from "../../i18n";
import { ReasonDialogView, useIntentWrite } from "../team/TeamDialogs";

type Common = { onClose: () => void; onReload: () => void; onDone: (message: string) => void };

export function GiveRoleDialog(
  props: Common & { role: StaffRoleListItem; candidates: readonly StaffMemberListItem[] },
) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [staffMemberId, setStaffMemberId] = useState("");
  const [reason, setReason] = useState("");
  const { role } = props;

  return (
    <ReasonDialogView
      title={t("roles.give.title")}
      body={
        <p className="meta">
          {role.name}
          <br />
          {t("roles.block.holdersHelp")}
        </p>
      }
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("roles.give.submit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          staffMemberId === "" ? t("roles.give.who") : null,
          (idempotencyKey) =>
            assignStaffRole(client, staffMemberId, role.staffRoleId, { reason: reason.trim() }, { idempotencyKey }),
          () => {
            const member = props.candidates.find((each) => each.staffMemberId === staffMemberId);
            props.onDone(t("roles.give.done", { name: member?.name ?? "", role: role.name }));
          },
        )
      }
    >
      <div className="field">
        <label htmlFor="give-who">{t("roles.give.who")}</label>
        <select
          id="give-who"
          value={staffMemberId}
          onChange={(event) => {
            write.edited();
            setStaffMemberId(event.currentTarget.value);
          }}
        >
          <option value="">—</option>
          {props.candidates.map((member) => (
            <option key={member.staffMemberId} value={member.staffMemberId}>
              {member.name}
            </option>
          ))}
        </select>
      </div>
    </ReasonDialogView>
  );
}

export function TakeRoleDialog(props: Common & { role: StaffRoleListItem; member: StaffMemberListItem }) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [reason, setReason] = useState("");
  const { role, member } = props;

  return (
    <ReasonDialogView
      title={t("roles.take.title")}
      body={
        <p className="meta">
          {member.name} · {role.name}
          <br />
          {t("roles.take.body")}
        </p>
      }
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("roles.take.submit")}
      danger
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          null,
          (idempotencyKey) =>
            removeStaffRole(
              client,
              member.staffMemberId,
              role.staffRoleId,
              { reason: reason.trim() },
              { idempotencyKey },
            ),
          () => props.onDone(t("roles.take.done", { name: member.name, role: role.name })),
        )
      }
    />
  );
}
